import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Globe, TrendingUp, Users, Building, MapPin, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const GlobalExpansion = () => {
  const globalMarkets = [
    {
      region: "North America",
      countries: ["United States", "Canada", "Mexico"],
      marketSize: "$2.1T",
      growth: "+15%",
      opportunities: ["Enterprise AI", "Healthcare Tech", "FinTech"],
      clients: 45,
      revenue: "$12M"
    },
    {
      region: "Europe",
      countries: ["UK", "Germany", "France", "Netherlands", "Spain"],
      marketSize: "$1.8T",
      growth: "+18%",
      opportunities: ["GDPR Compliance", "Green Tech", "Digital Banking"],
      clients: 38,
      revenue: "$9.5M"
    },
    {
      region: "Asia Pacific",
      countries: ["Singapore", "Japan", "Australia", "India", "South Korea"],
      marketSize: "$1.6T",
      growth: "+22%",
      opportunities: ["Mobile First", "E-commerce", "Smart Cities"],
      clients: 32,
      revenue: "$8.2M"
    },
    {
      region: "Africa",
      countries: ["Nigeria", "South Africa", "Kenya", "Ghana", "Morocco"],
      marketSize: "$850B",
      growth: "+35%",
      opportunities: ["Mobile Banking", "AgriTech", "EdTech"],
      clients: 28,
      revenue: "$6.8M"
    },
    {
      region: "Middle East",
      countries: ["UAE", "Saudi Arabia", "Qatar", "Kuwait"],
      marketSize: "$450B",
      growth: "+28%",
      opportunities: ["Smart Cities", "Oil & Gas Tech", "Tourism"],
      clients: 18,
      revenue: "$4.2M"
    },
    {
      region: "Latin America",
      countries: ["Brazil", "Argentina", "Chile", "Colombia"],
      marketSize: "$380B",
      growth: "+25%",
      opportunities: ["FinTech", "E-commerce", "AgriTech"],
      clients: 22,
      revenue: "$5.1M"
    }
  ];

  const successStories = [
    {
      company: "Client in UK",
      location: "London, UK",
      industry: "Financial Services",
      challenge: "Digital transformation across 45 countries",
      solution: "AI-powered customer service automation",
      results: "40% cost reduction, 95% customer satisfaction",
      timeline: "8 weeks implementation"
    },
    {
      company: "Client in Singapore",
      location: "Singapore",
      industry: "E-commerce",
      challenge: "Scale AI operations across Asia Pacific",
      solution: "Custom ML models for recommendation engine",
      results: "300% increase in conversion rates",
      timeline: "6 weeks delivery"
    },
    {
      company: "Client in Nigeria",
      location: "Lagos, Nigeria",
      industry: "Oil & Gas",
      challenge: "Optimize supply chain across Africa",
      solution: "Predictive analytics and IoT integration",
      results: "50% reduction in downtime, $15M savings",
      timeline: "10 weeks rollout"
    }
  ];

  const globalAdvantages = [
    {
      title: "24/7 Global Support",
      description: "Round-the-clock support across all time zones with local language expertise",
      icon: Globe
    },
    {
      title: "Local Market Knowledge",
      description: "Deep understanding of regulatory requirements and cultural nuances in 50+ countries",
      icon: MapPin
    },
    {
      title: "Fastest Global Deployment",
      description: "Deploy AI solutions simultaneously across multiple countries in weeks, not years",
      icon: TrendingUp
    },
    {
      title: "Cultural Adaptation",
      description: "AI models trained on local data with cultural and linguistic customization",
      icon: Users
    },
    {
      title: "Compliance Expertise",
      description: "Built-in compliance for GDPR, CCPA, local data protection laws in every region",
      icon: Building
    },
    {
      title: "Proven Global Success",
      description: "200+ successful international deployments across Fortune 500 companies",
      icon: Star
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
        <div className="max-w-7xl mx-auto text-center">
          <Badge variant="outline" className="mb-4">
            <Globe className="w-4 h-4 mr-2" />
            Global Market Leader
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Global AI Transformation at Scale
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-4xl mx-auto mb-8">
            The only AI consulting firm that delivers enterprise transformation simultaneously 
            across 50+ countries. Beat global competitors with local expertise.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg">
                Start Global Expansion
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="#global-success-stories">
              <Button size="lg" variant="outline">
                View Global Case Studies
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Global Market Overview */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              $7.2T Global Digital Transformation Market
            </h2>
            <p className="text-xl text-muted-foreground">
              QuantumBox AI operates in 6 continents with local expertise and global scale
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {globalMarkets.map((market, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <CardTitle className="text-xl">{market.region}</CardTitle>
                    <Badge variant="default">{market.growth} Growth</Badge>
                  </div>
                  <CardDescription>
                    Market Size: <span className="font-bold text-primary">{market.marketSize}</span>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium mb-2">Key Countries:</p>
                      <div className="flex flex-wrap gap-1">
                        {market.countries.map((country, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {country}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium mb-2">Growth Opportunities:</p>
                      <ul className="text-sm space-y-1">
                        {market.opportunities.map((opp, idx) => (
                          <li key={idx} className="flex items-center">
                            <TrendingUp className="w-3 h-3 mr-2 text-primary" />
                            {opp}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="pt-3 border-t">
                      <div className="flex justify-between text-sm">
                        <span>Active Clients:</span>
                        <span className="font-bold">{market.clients}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Annual Revenue:</span>
                        <span className="font-bold text-primary">{market.revenue}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Global Success Stories */}
      <section id="global-success-stories" className="py-16 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Global Success Stories
            </h2>
            <p className="text-xl text-muted-foreground">
              How we're transforming businesses across continents
            </p>
          </div>
          
          <div className="space-y-6">
            {successStories.map((story, index) => (
              <Card key={index} className="overflow-hidden">
                <CardHeader className="bg-primary/5">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl">{story.company}</CardTitle>
                      <CardDescription className="flex items-center mt-1">
                        <MapPin className="w-4 h-4 mr-1" />
                        {story.location} • {story.industry}
                      </CardDescription>
                    </div>
                    <Badge variant="outline">{story.timeline}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <h4 className="font-semibold mb-2 text-red-600">Challenge</h4>
                      <p className="text-sm text-muted-foreground">{story.challenge}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2 text-blue-600">Solution</h4>
                      <p className="text-sm text-muted-foreground">{story.solution}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2 text-green-600">Results</h4>
                      <p className="text-sm text-muted-foreground">{story.results}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Global Advantages */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Global Enterprises Choose QuantumBox AI
            </h2>
            <p className="text-xl text-muted-foreground">
              The only AI firm that truly understands global complexity
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {globalAdvantages.map((advantage, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <advantage.icon className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{advantage.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{advantage.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready for Global AI Transformation?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join 200+ global enterprises using QuantumBox AI to dominate their markets worldwide. 
            Start your global expansion today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" variant="secondary">
                Schedule Global Strategy Session
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/quantumbox-guide">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
                Download Global Expansion Guide
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GlobalExpansion;